<template>
  <v-dialog
    :model-value="modelValue"
    max-width="540"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-5">
      <page-title :value="$t('aiReport.bulkGenerateTitle')" />

      <p class="mt-3 text-body-2">
        {{
          $t('aiReport.targetQuarter', {
            months: monthsLabel,
            year: businessYear,
            quarter,
          })
        }}
      </p>

      <v-alert
        variant="outlined"
        color="primary"
        icon="mdi-information-outline"
        class="mt-2"
        density="compact"
      >
        <ul class="ma-0 pl-4 text-body-2">
          <li>{{ $t('aiReport.bulkHints.newReports') }}</li>
          <li>{{ $t('aiReport.bulkHints.overwriteDrafts') }}</li>
          <li>{{ $t('aiReport.bulkHints.skipPublished') }}</li>
          <li>{{ $t('aiReport.bulkHints.async') }}</li>
        </ul>
      </v-alert>

      <div v-if="batch" class="mt-4">
        <p class="text-body-2 mb-1">
          {{
            $t('aiReport.generatingProgress', {
              success: batch.successCount,
              total: batch.totalCount,
            })
          }}
          <span v-if="batch.failedCount > 0" class="text-red">
            {{ $t('aiReport.failedCount', { count: batch.failedCount }) }}
          </span>
        </p>
        <v-progress-linear
          :model-value="progressPercent"
          :indeterminate="batch.status === 'processing' && batch.successCount === 0"
          color="primary"
          height="14"
          rounded
        >
          <template v-slot:default>
            <span class="text-white text-caption">{{ progressPercent }}%</span>
          </template>
        </v-progress-linear>
        <p v-if="batch.status === 'completed'" class="mt-3 text-body-2 text-success">
          <v-icon size="small" color="success">mdi-check-circle</v-icon>
          {{ $t('aiReport.generationCompleted') }}
        </p>
        <p v-if="batch.status === 'failed'" class="mt-3 text-body-2 text-red">
          <v-icon size="small" color="red">mdi-alert-circle</v-icon>
          {{ $t('aiReport.generationFailed') }}
        </p>
      </div>

      <div class="d-flex mt-5" style="gap: 0.5rem;">
        <v-spacer />
        <v-btn variant="text" @click="close" :disabled="isStarting">
          {{
            batch && batch.status === 'completed'
              ? $t('common.close')
              : $t('common.cancel')
          }}
        </v-btn>
        <v-btn
          v-if="!batch"
          color="primary"
          variant="flat"
          :loading="isStarting"
          @click="$emit('start')"
        >
          {{ $t('aiReport.bulkGenerateStart') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import type { AiReportBatchResponse, AiReportQuarter } from "@/api/ApiAiReport";

export default defineComponent({
  name: "AiReportBulkGenerateDialog",
  components: { PageTitle },
  props: {
    modelValue: { type: Boolean, default: false },
    businessYear: { type: Number, required: true },
    quarter: { type: Number as PropType<AiReportQuarter>, required: true },
    monthsLabel: { type: String, required: true },
    batch: {
      type: Object as PropType<AiReportBatchResponse | null>,
      default: null,
    },
    isStarting: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "start", "close"],
  computed: {
    progressPercent(): number {
      if (!this.batch || this.batch.totalCount === 0) return 0;
      return Math.floor((this.batch.successCount / this.batch.totalCount) * 100);
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
  },
});
</script>
